import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { applicationMessages, toasterMessages } from '@messages/messages';
import * as ace from "ace-builds";
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { environment } from 'src/environments/environment';
import { CandidateService } from '../candidate.service';
import { configureAceSession } from 'src/app/shared/ace-mode-utils';
export interface DialogData {
  details: any;
  _id: string;
  assessmentid: string;
  templateName: string;
  templateType: string;
  content: any;
  heading: string;
  update: boolean;
  subject: string;
  templatePlaceholder: string;
  languageCode: number;
  isEnterpriseUser: string;
}

@Component({
  selector: 'app-coding-question-view',
  templateUrl: './coding-question-view.component.html',
  styleUrls: ['./coding-question-view.component.scss']
})

export class CodingQuestionViewComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild("verifyCodeEditor") private verifyCodeEditor: ElementRef<HTMLElement>;
  @Output() nextQuestion = new EventEmitter<any>();

  config: AngularEditorConfig = {
    height: 'auto',
    minHeight: '0',
    width: 'auto',
    minWidth: '0',
    showToolbar: false
  };

  options = {
    autoRefresh: true,
    fixedGutter: true,
    indentWithTabs: true,
    lineNumbers: true,
    lineWrapping: true,
    lint: true,
    smartIndent: false,
    theme: "material",
    mode: 'javascript'
  }

  verifyCodeAceEditor: any;
  runCompleted: boolean = false;
  runTestResult: any;
  codingLanguages: any;
  selectedLang: any;
  candidateSelectedLanguage: any;
  partialSaveInterval: any;
  prevAnswer: any;
  isAutoSaveInprogress: boolean = false;
  btnRunCodeTxt = 'Run';
  condingInstructions: any = '';
  formattedQuestionText: string = '';
  isSubmitting: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CodingQuestionViewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private toastr: ToastrService,
    public dialog: MatDialog,
    private candidateService: CandidateService) { }

  ngOnInit(): void {
    this.codingLanguages = this.data?.details?.question?.questionSignatures;
    this.selectedLang = this.codingLanguages[0];
    this.candidateSelectedLanguage = this.codingLanguages[0].questionSignatureId;
    this.prevAnswer = this.data?.details?.question?.answer;
    this.formattedQuestionText = this.formatQuestionText(this.data?.details?.question?.questionText || '');
  }

  ngOnDestroy(): void {
    if (this.partialSaveInterval) clearInterval(this.partialSaveInterval);
  }

  onLanguageSelection(event: any): void {
    const codeValue = this.prevAnswer || event?.value?.initialCode;
    this.verifyCodeAceEditor.session.setValue(this.convertEscapedNewlines(codeValue, event?.value?.code_language, event?.value?.language_id));
    configureAceSession(this.verifyCodeAceEditor.session, event?.value?.code_language);
    this.candidateSelectedLanguage = event?.value?.questionSignatureId;
    this.selectedLang = event?.value;
    this.condingInstructions = event?.value?.instructions;
    // Reset output when switching languages to avoid showing stale results
    this.runTestResult = null;
    this.runCompleted = false;
  }

  /**
   * Convert escaped newlines (\\n as text) to actual newlines, except for C/C++
   * to preserve string literals like printf("\\n").
   */
  convertEscapedNewlines(code: string, codeLanguage?: string, languageId?: number): string {
    if (!code) return '';
    const lang = String(codeLanguage || '').toLowerCase();
    const isCandCpp = [50, 54].includes(Number(languageId)) || /\bc\+\+|cpp\b|^c$/.test(lang);
    if (isCandCpp) return code;
    return code.replace(/\\n/g, '\n');
  }

  formatQuestionText(text: string): string {
    if (!text) return '';
    let formatted = this.convertEscapedNewlines(text);

    // 1. Strip triple backtick wrappers if they exist
    formatted = formatted.replace(/^```[a-z]*\n([\s\S]*?)\n```$/g, '$1');
    formatted = formatted.replace(/^```[\s\S]*?```$/g, (match) => match.replace(/```/g, ''));

    // 2. Format Headers (# or ##) -> Bold + Newline
    formatted = formatted.replace(/^#+\s*(.*)$/gm, '<b>$1</b>');

    // 3. Handle Markdown bold: **text** -> <b>text</b>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

    // 4. Handle Inline Code: `code` -> <code>code</code>
    formatted = formatted.replace(/`(.*?)`/g, '<code style="background: #f1f5f9; padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 0.9em; color: #e11d48;">$1</code>');

    // 5. Handle Bullet Points: "- " or "* " at start of line -> Bullet
    formatted = formatted.replace(/^\s*[\-\*]\s+/gm, '&bull; ');

    // 6. Handle Newlines: \n -> <br>
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
  }

  ngAfterViewInit(): void {
    ace.config.set("fontSize", "14px");
    ace.config.set('basePath', 'https://unpkg.com/ace-builds@1.4.12/src-noconflict');
    this.verifyCodeAceEditor = ace.edit(this.verifyCodeEditor.nativeElement);
    this.verifyCodeAceEditor.setTheme('ace/theme/solarized_dark');
    this.onLanguageSelection({ value: this.selectedLang })
    if (!this.data?.isEnterpriseUser) {
      this.partialSaveInterval = setInterval(() => { this.sendPartialSave() }, environment.candidateAutosaveMs);
    }
  }

  sendPartialSave(): void {
    // Skip auto-save if already in progress or if it's enterprise user
    if (this.isAutoSaveInprogress || this.data?.isEnterpriseUser) {
      return;
    }

    this.isAutoSaveInprogress = true;
    const ans = this.verifyCodeAceEditor?.getValue();
    const codeLang = this.candidateSelectedLanguage;

    // Skip if no meaningful code changes
    if (!ans || ans.trim() === '') {
      this.isAutoSaveInprogress = false;
      return;
    }

    const reqObj = {
      question: {
        question: this.data?.details?.question?.id,
        answer: ans,
        questionSignature_id: codeLang
      },
      partialSave: true
    };

    this.candidateService.getNextQuestion(
      this.data?.details?.question?.questionType,
      reqObj,
      this.data?.assessmentid
    ).subscribe(
      (response: any) => {
        this.isAutoSaveInprogress = false;
        // Handle preview response silently (no need to show success for auto-save)
        if (response?.preview) {
          // Preview mode: auto-save skipped
        }
      },
      (error) => {
        this.isAutoSaveInprogress = false;
        // Only show error if it's not a preview mode issue
        if (!error?.error?.preview) {
          console.warn('Auto-save failed:', error);
          // Don't show toast for auto-save failures to avoid interrupting user
        }
      }
    );
  }

  closeCodingQuestion(): void {

    const deleteDialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'delete-dialog-container',
      data: { danger: false, messageBody: applicationMessages.closeCodingEditor }
    });

    deleteDialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        this.submitQuestion(true);
      }
    });

  }

  runTestCases(): void {
    this.btnRunCodeTxt = 'Code Running';
    let reqData = {
      questionSignature_id: this.candidateSelectedLanguage,
      language_id: this.selectedLang.language_id,
      source_code: this.convertEscapedNewlines(this.verifyCodeAceEditor?.getValue(), this.selectedLang?.code_language, this.selectedLang?.language_id),
      questionId: this.data?.details?.question?.id,
      assessmentId: this.data?.assessmentid
    };

    this.candidateService.runTests(reqData).subscribe((res) => {
      this.btnRunCodeTxt = 'Run';
      this.runTestResult = res.output || res.errorMessage;
      this.runCompleted = true;
    }, (error) => {
      this.btnRunCodeTxt = 'Run';
      this.runTestResult = error?.error?.errors[0]?.error;
    })

  }


  submitQuestion(autoSubmit: boolean = false): void {
    if (this.isSubmitting) return;

    if (!autoSubmit) {
      if (!this.runCompleted && !this.runTestResult) {
        this.toastr.warning(toasterMessages.submitQuestionWarning, '');
        return;
      }
      let errormsg = '';
      if (this.runTestResult?.errorMessage) {
        errormsg = toasterMessages.submitAssessment.ZeroTestCasePassed;
      } else if (this.runTestResult?.failure || this.runTestResult?.errored) {
        errormsg = toasterMessages.submitAssessment.totalTestCasePassed.replace('{runTestResult}', `${this.runTestResult?.success}`);
      } else {
        errormsg = toasterMessages.submitAssessment.submitConfirm;
      }

      const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'delete-dialog-container',
        data: { danger: false, messageBody: errormsg }
      });

      confirmDialogRef.afterClosed().subscribe(result => {
        if (result === 'confirm') {
          if (this.isSubmitting) return;
          this.isSubmitting = true;

          let requestObj = {
            language_id: this.selectedLang?.questionSignatureId,
            source_code: this.convertEscapedNewlines(this.verifyCodeAceEditor?.getValue(), this.selectedLang?.code_language, this.selectedLang?.language_id),
          }
          if (this.partialSaveInterval) {
            clearInterval(this.partialSaveInterval);
          }
          this.nextQuestion.emit(requestObj);
        }
      });
    } else {
      this.isSubmitting = true;
      let requestObj = {
        language_id: this.selectedLang?.questionSignatureId,
        source_code: this.convertEscapedNewlines(this.verifyCodeAceEditor?.getValue(), this.selectedLang?.code_language, this.selectedLang?.language_id),
      }
      if (this.partialSaveInterval) {
        clearInterval(this.partialSaveInterval);
      }
      this.nextQuestion.emit(requestObj);
    }
  }

  handleEvent(e) {
    if (e.action === 'notify') {
      this.toastr.warning(toasterMessages.candidateAssessmentTimeWarning.replace('{timeLeft}', `${Math.floor((e.left / 1000) / 60)}`));
    }
    if (e.action === 'done') {
      // Check if this is the last coding question
      const isLastQuestion = this.data?.details?.currentQuestion === this.data?.details?.totalquestions;

      if (isLastQuestion) {
        // Last question - submit the question AND the entire coding module
        // Don't show generic "time's up" toast - specific success toasts will show
        this.autoSubmitCodingModule();
      } else {
        // Not the last question - show timer expiry message and move to next
        this.toastr.info(toasterMessages.candidateAssessmentInfo);
        this.submitQuestion(true);
      }
    }
  }

  private autoSubmitCodingModule(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    if (this.data?.isEnterpriseUser) {
      this.isSubmitting = false; // Reset flag before closing
      this.dialogRef.close({ moduleSubmitted: false });
      return;
    }

    const performSubmit = () => {
      // Submit the entire coding module when module timer expires
      this.candidateService.submitAssessment('CODING', this.data?.assessmentid)
        .subscribe(
          (res: any) => {
            this.toastr.success(toasterMessages.submitAssessmentModuleSuccess.replace('{module}', 'Coding'));

            // Check if this was the last module and auto-submit overall assessment
            if (res?.canDoFinalSubmit) {
              // Auto-submit overall assessment since all modules are now completed
              this.candidateService.submitAssessment('', this.data?.assessmentid)
                .subscribe(
                  (finalRes: any) => {
                    this.isSubmitting = false; // Reset flag on success
                    this.toastr.success(toasterMessages.submitAssessmentSuccess);
                    this.dialogRef.close({ moduleSubmitted: true, finalSubmitted: true, res: finalRes });
                  },
                  (err) => {
                    this.isSubmitting = false; // Reset flag on error
                    this.toastr.error(err?.error?.errors?.[0]?.error || 'Failed to submit overall assessment', 'Error');
                    this.dialogRef.close({ moduleSubmitted: true, finalSubmitted: false, error: err });
                  }
                );
            } else {
              // Not the last module, just close dialog and return to categories
              this.isSubmitting = false; // Reset flag on success
              this.dialogRef.close({ moduleSubmitted: true, res });
            }
          },
          (err) => {
            this.isSubmitting = false; // Reset flag on error
            this.toastr.error(err?.error?.errors?.[0]?.error || 'Failed to submit coding module', 'Error');
            this.dialogRef.close({ moduleSubmitted: false, error: err });
          }
        );
    };

    // Save current work before submitting
    const ans = this.verifyCodeAceEditor?.getValue();
    const codeLang = this.candidateSelectedLanguage;

    if (ans && ans.trim() !== '' && this.data?.details?.question?.id) {
      const reqObj = {
        question: {
          question: this.data?.details?.question?.id,
          answer: ans,
          questionSignature_id: codeLang
        },
        partialSave: true
      };

      this.candidateService.getNextQuestion(
        this.data?.details?.question?.questionType,
        reqObj,
        this.data?.assessmentid
      ).subscribe(
        () => performSubmit(),
        () => performSubmit() // Proceed even on error
      );
    } else {
      performSubmit();
    }
  }
}
